'use client';

import {
  FrontendUserImpression,
  UserImpressionType,
} from '@trucksmarter/shared';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import saveUserImpression from '../../../_lib/api/client/save-user-impression';

const ImpressionsContext = createContext<Map<
  UserImpressionType,
  FrontendUserImpression
> | null>(null);
const SaveImpressionContext = createContext<
  | ((type: UserImpressionType, hasAccepted?: boolean | null) => Promise<void>)
  | null
>(null);

export interface UseImpressionReturn {
  data: FrontendUserImpression | undefined;
  bumpViewCount: () => Promise<void>;
  accept: () => Promise<void>;
  decline: () => Promise<void>;
}

export function useImpression(type: UserImpressionType): UseImpressionReturn {
  const userImpressions = useContext(ImpressionsContext);
  const saveImpression = useContext(SaveImpressionContext);

  if (userImpressions === null || saveImpression === null) {
    throw new Error(
      'useImpression has to be used within <ImpressionsProvider>',
    );
  }

  const bumpViewCount = useCallback(
    () => saveImpression?.(type),
    [saveImpression, type],
  );
  const accept = useCallback(
    () => saveImpression?.(type, true),
    [saveImpression, type],
  );
  const decline = useCallback(
    () => saveImpression?.(type, false),
    [saveImpression, type],
  );

  return {
    data: userImpressions.get(type),
    bumpViewCount,
    accept,
    decline,
  };
}

interface Props {
  data: FrontendUserImpression[];
}

export default function ImpressionsProvider({
  data,
  children,
}: PropsWithChildren<Props>) {
  const [impressions, setImpressions] = useState<
    Map<UserImpressionType, FrontendUserImpression>
  >(() => createImpressionsMap(data));

  useEffect(() => {
    setImpressions(createImpressionsMap(data));
  }, [data]);

  const saveImpression = useCallback(
    async (type: UserImpressionType, hasAccepted: boolean | null = null) => {
      const userImpression = await saveUserImpression({
        userImpressionType: type,
        hasAccepted,
      });
      setImpressions((prevImpressions) =>
        new Map(prevImpressions).set(type, userImpression),
      );
    },
    [],
  );

  return (
    <ImpressionsContext.Provider value={impressions}>
      <SaveImpressionContext.Provider value={saveImpression}>
        {children}
      </SaveImpressionContext.Provider>
    </ImpressionsContext.Provider>
  );
}

function createImpressionsMap(
  data: FrontendUserImpression[],
): Map<UserImpressionType, FrontendUserImpression> {
  return new Map(
    data.map((impression) => [
      impression.userImpressionType as UserImpressionType,
      impression,
    ]),
  );
}
